/* ===========================================================================
 * Filter Persistence
 * ---------------------------------------------------------------------------
 * Mantém os filtros escolhidos pelo cliente ao navegar entre coleções.
 *
 * Ex.: filtrou "Tamanho: M" e "Cor: Preto" em /collections/praia, foi para
 * /collections/casual -> os mesmos filtros são reaplicados lá.
 *
 * ------------------------------------------------------------------------
 * COMO a restauração é feita (e por que NÃO reconstruímos a URL)
 * ------------------------------------------------------------------------
 * A versão anterior montava a query string a partir de `location.search` e
 * anexava os filtros salvos. Isso tinha um furo grave: nem sempre a URL está
 * completa quando este script roda.
 *
 * O `filter-availability.js` garante "Em estoque" injetando
 * `filter.v.availability` na URL com um REDIRECT de página inteira, disparado de
 * um script no <head>. Só que um redirect iniciado ali não interrompe o parsing:
 * o documento continua sendo montado e os scripts `defer` — inclusive este —
 * ainda executam, num documento condenado cuja URL ainda NÃO tem o filtro de
 * estoque. Copiar essa URL produzia um render sem `filter.v.availability`, e
 * produtos esgotados voltavam para a vitrine.
 *
 * Agora a restauração não olha para a URL. Ela:
 *
 *   1. marca os CHECKBOXES nativos dos filtros salvos (em todos os formulários);
 *   2. garante o filtro de estoque forçado, também no checkbox;
 *   3. serializa o formulário — do mesmo jeito que o `onSubmitHandler` do
 *      facets.js faz — e manda esse resultado para `FacetFiltersForm.renderPage`.
 *
 * Assim `filter.v.availability`, `q` e `sort_by` vêm do próprio formulário, e o
 * resultado é correto independentemente do estado da URL.
 *
 * Regras de ouro (as mesmas da Section de chips):
 *   - o filtro nativo continua sendo a única fonte de verdade; este módulo não
 *     filtra nada, só reaplica uma seleção anterior;
 *   - só é restaurado o que EXISTE na nova página. Se "Cor: Preto" não é opção em
 *     Casual, o filtro é descartado — nunca geramos uma URL que devolveria zero
 *     produtos;
 *   - se a URL já vem filtrada (link direto, voltar/avançar), ela vence.
 *
 * Para desativar, remova a tag <script> deste arquivo das sections
 * main-collection e main-search.
 * =========================================================================== */

(function () {
  'use strict';

  const STORAGE_KEY = 'sahara:active-filters';

  // Faixas de preço não são portáveis entre coleções (o mín/máx muda, e um
  // intervalo de uma coleção pode não fazer sentido na outra).
  const PRICE_PREFIX = 'filter.p.price.';

  /** Regra publicada pelo filter-availability.js. */
  function forcedFilter() {
    return window.ForcedAvailabilityFilter || null;
  }

  /* -------------------------------------------------------------------------
   * Armazenamento (sessionStorage: dura a sessão de navegação, não "para sempre")
   * ---------------------------------------------------------------------- */

  /**
   * A disponibilidade é gerenciada pelo filter-availability.js, não pelo cliente:
   * fica fora do que persistimos.
   */
  function isPortableFilter(name) {
    const forced = forcedFilter();

    return (
      name.indexOf('filter.') === 0 &&
      name.indexOf(PRICE_PREFIX) !== 0 &&
      (!forced || name !== forced.param)
    );
  }

  function save(searchParams) {
    const entries = [];

    new URLSearchParams(searchParams || '').forEach((value, name) => {
      if (isPortableFilter(name)) entries.push([name, value]);
    });

    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
    } catch (error) {
      // Modo privado / storage desabilitado: o recurso apenas não persiste.
      console.error('[filter-persistence] Não foi possível salvar os filtros.', error);
    }
  }

  function load() {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];

      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error('[filter-persistence] Não foi possível ler os filtros salvos.', error);
      return [];
    }
  }

  /* -------------------------------------------------------------------------
   * Acesso ao filtro nativo
   * ---------------------------------------------------------------------- */

  function nativeForms() {
    return Array.from(document.querySelectorAll('facet-filters-form'));
  }

  /**
   * Um filtro só pode ser restaurado se a página atual realmente o oferece — isto
   * é, se existe um checkbox nativo com aquele `name`/`value`. Procuramos pelos
   * contratos do Shopify (param_name + value), não por marcação do tema.
   */
  function findNativeInputs(name, value) {
    const selector = `input[type="checkbox"][name="${CSS.escape(
      name
    )}"][value="${CSS.escape(value)}"]`;

    return nativeForms()
      .map(form => form.querySelector(selector))
      .filter(Boolean);
  }

  /**
   * O formulário a serializar. O `<select name="sort_by">` vive apenas no
   * formulário do topo; escolhê-lo preserva a ordenação atual.
   */
  function submitForm() {
    const forms = nativeForms()
      .map(element => element.querySelector('form'))
      .filter(Boolean);

    return forms.find(form => form.querySelector('[name="sort_by"]')) || forms[0] || null;
  }

  /**
   * Serializa um formulário de filtros do mesmo modo que o `onSubmitHandler` do
   * facets.js: checkboxes só entram se marcados, os demais campos entram com o
   * valor atual. É daqui que saem `filter.v.availability`, `q` e `sort_by`.
   */
  function serializeForm(form) {
    const params = new URLSearchParams();

    Array.from(form.elements).forEach(element => {
      if (!element.name || element.disabled) return;

      if (element.type === 'checkbox' || element.type === 'radio') {
        if (element.checked) params.append(element.name, element.value);
        return;
      }

      if (element.value !== '') params.append(element.name, element.value);
    });

    return params;
  }

  /**
   * Marca o checkbox do filtro de estoque forçado, em todos os formulários.
   *
   * É este passo que torna a restauração imune ao momento do redirect: mesmo que
   * a URL ainda não tenha `filter.v.availability`, a serialização terá.
   */
  function ensureForcedFilters() {
    const forced = forcedFilter();
    if (!forced) return;

    findNativeInputs(forced.param, forced.value).forEach(input => {
      input.checked = true;
    });
  }

  /* -------------------------------------------------------------------------
   * Restauração
   * ---------------------------------------------------------------------- */

  function urlHasFilters() {
    let found = false;

    new URLSearchParams(window.location.search).forEach((value, name) => {
      if (isPortableFilter(name)) found = true;
    });

    return found;
  }

  function restore(stored) {
    if (!stored.length) return false;

    // A URL manda: link filtrado, voltar/avançar, etc.
    if (urlHasFilters()) return false;

    const form = submitForm();
    if (!form) return false;

    const restored = [];

    stored.forEach(entry => {
      const inputs = findNativeInputs(entry[0], entry[1]);

      // Filtro inexistente nesta coleção: descartado silenciosamente.
      if (!inputs.length) return;

      // Valor sem nenhum produto aqui: aplicá-lo esvaziaria a vitrine.
      if (inputs[0].disabled) return;

      inputs.forEach(input => {
        input.checked = true;
        restored.push(input);
      });
    });

    if (!restored.length) return false;

    ensureForcedFilters();

    const params = serializeForm(form);

    // Um resultado filtrado é outro conjunto — a página em que estávamos não vale.
    params.delete('page');

    const searchParams = params.toString();

    // Avisa a interface (a Section de chips escuta `change` nos checkboxes
    // nativos) para que os chips já nasçam marcados. É um evento por checkbox
    // restaurado, e nenhum deles dispara requisição: o facets.js escuta `input`,
    // não `change`.
    restored.forEach(input => {
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    // Reusa o AJAX do próprio tema: troca grid, contagem, paginação e filtros,
    // sem recarregar a página. `updateURLHash: false` porque a versão filtrada
    // SUBSTITUI a que o servidor acabou de entregar — empilhar uma entrada faria
    // o botão voltar trazer o cliente de volta para a mesma coleção sem filtro.
    FacetFiltersForm.renderPage(searchParams, null, false);
    history.replaceState(
      { searchParams },
      '',
      `${window.location.pathname}?${searchParams}`
    );

    return true;
  }

  /* -------------------------------------------------------------------------
   * Bootstrap
   * ---------------------------------------------------------------------- */

  function init() {
    // facets.js é carregado condicionalmente pelas sections.
    if (typeof FacetFiltersForm === 'undefined') return;
    if (!document.querySelector('facet-filters-form')) return;

    // Documento condenado: o filter-availability.js já iniciou um redirect de
    // página inteira. Não restauramos (seria desfeito no instante seguinte) e,
    // sobretudo, NÃO SALVAMOS — salvar aqui gravaria uma lista vazia e apagaria
    // justamente os filtros que o cliente trouxe da coleção anterior.
    const forced = forcedFilter();
    if (forced && forced.redirecting) return;

    const stored = load();

    // Decorator (não um override): `renderPage` é o funil por onde passam TODAS
    // as mudanças de filtro — chip, checkbox nativo, "limpar tudo", ordenação e
    // voltar/avançar. Chamamos a implementação nativa intacta e só registramos o
    // estado. "Limpar tudo" grava uma lista vazia, e é assim que o cliente
    // desliga a persistência: limpando os filtros.
    const nativeRenderPage = FacetFiltersForm.renderPage;

    FacetFiltersForm.renderPage = function (searchParams) {
      save(searchParams);
      return nativeRenderPage.apply(this, arguments);
    };

    // Se nada foi restaurado, o estado atual da URL é o que vale registrar.
    if (!restore(stored)) {
      save(window.location.search.slice(1));
    }
  }

  // Scripts `defer` rodam com readyState já em "interactive" — o DOMContentLoaded
  // só vem depois de TODOS eles. Esperá-lo garante que o facets.js já executou e
  // que a Section de chips já registrou seus listeners.
  if (document.readyState === 'complete') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  }
})();
